import QRCode from 'qrcode'
import mersadUrl from '../assets/fonts/mersad.woff2'
import { formatDateRange, formatTimeRange } from './translations'
import { localizeEventType } from './eventTypes'

// Export a one-page, branded invitation as a PDF.
//
// We render to a print window (Save as PDF) rather than drawing with jsPDF:
// jsPDF's built-in fonts can't render Georgian, but the browser can, so this
// keeps EN + KA invites correct. A QR code (to the invite link) is generated
// with `qrcode` and embedded as a data URL.

const esc = (s: unknown): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const detailRow = (label: string, value: string) =>
  `<div class="detail"><div class="label">${label}</div><div class="value">${value}</div></div>`

export async function exportInvitePdf(
  event: any,
  inviteUrl: string,
  lang: string = 'en',
): Promise<void> {
  // Open synchronously (within the click) so pop-up blockers don't stop us.
  const win = window.open('', '_blank')
  if (!win) throw new Error('popup-blocked')
  // The print window is about:blank, so it has no base URL to resolve the
  // bundled font against — and index.css is not loaded in it either.
  const mersad = new URL(mersadUrl, window.location.origin).href
  win.document.write(
    '<!doctype html><title>…</title><body style="font:16px sans-serif;padding:40px;color:#15140F">Generating…</body>',
  )

  const t = (en: string, ka: string) => (lang === 'ka' ? ka : en)
  const qr = await QRCode.toDataURL(inviteUrl, {
    width: 360,
    margin: 1,
    color: { dark: '#15140F', light: '#ffffff' },
  })

  const dateStr = formatDateRange(event.date, event.end_date, lang)
  const timeStr = formatTimeRange(event.time, event.end_time)
  const typeStr = localizeEventType(event.event_type, lang)

  const rows: string[] = []
  if (dateStr) rows.push(detailRow(t('Date', 'თარიღი'), esc(dateStr)))
  if (timeStr) rows.push(detailRow(t('Time', 'დრო'), esc(timeStr)))
  if (event.location) rows.push(detailRow(t('Location', 'მისამართი'), esc(event.location)))
  if (event.honoree_name)
    rows.push(detailRow(t('Guest of honor', 'ღირსეული სტუმარი'), esc(event.honoree_name)))

  const html = `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8">
<title>${esc(event.title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..800&family=Noto+Sans+Georgian:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  /* Mersad is the Georgian face that matches Bricolage Grotesque in the app
     (index.css). Without it a Georgian PDF prints in a system fallback that
     looks nothing like the same invitation on screen. */
  @font-face {
    font-family: 'Mersad';
    src: url('${mersad}') format('woff2');
    font-weight: 100 900;
    unicode-range: U+10A0-10FF, U+1C90-1CBF, U+2D00-2D2F, U+20BE;
  }
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Bricolage Grotesque','Mersad','Noto Sans Georgian',sans-serif; color: #15140F; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 210mm; min-height: 297mm; padding: 26mm 22mm; margin: 0 auto; position: relative; }
  .bar { position: absolute; top: 0; left: 0; right: 0; height: 10mm; background: #E8452A; }
  .eyebrow { letter-spacing: .28em; text-transform: uppercase; color: #E8452A; font-weight: 600; font-size: 12px; }
  h1 { font-size: 40px; line-height: 1.05; letter-spacing: -.02em; margin: 8px 0 4px; }
  .type { color: #8A877E; font-size: 15px; margin-bottom: 14px; }
  .detail { display: flex; gap: 14px; padding: 10px 0; border-top: 1px solid rgba(21,20,15,.1); }
  .detail .label { width: 120px; color: #8A877E; font-size: 13px; flex: none; }
  .detail .value { font-weight: 600; font-size: 16px; }
  .desc { margin-top: 16px; color: #4B4A44; font-size: 15px; white-space: pre-line; }
  .qr { margin-top: 28px; display: flex; align-items: center; gap: 18px; }
  .qr img { width: 34mm; height: 34mm; }
  .qr .cap b { display: block; color: #15140F; font-size: 16px; margin-bottom: 2px; }
  .qr .cap { font-size: 13px; color: #4B4A44; word-break: break-all; }
  .footer { position: absolute; bottom: 16mm; left: 22mm; right: 22mm; color: #8A877E; font-size: 12px; border-top: 1px solid rgba(21,20,15,.1); padding-top: 10px; }
</style>
</head>
<body>
  <div class="page">
    <div class="bar"></div>
    <div class="eyebrow" style="margin-top:14mm">${t("You're Invited", 'მოწვეული ხართ')}</div>
    <h1>${esc(event.title)}</h1>
    <div class="type">${esc(typeStr)}</div>
    ${rows.join('')}
    ${event.description ? `<div class="desc">${esc(event.description)}</div>` : ''}
    ${
      event.arrival_instructions
        ? `<div class="detail"><div class="label">${t('Getting there', 'როგორ მოხვიდე')}</div><div class="value" style="font-weight:400;color:#4B4A44;white-space:pre-line">${esc(event.arrival_instructions)}</div></div>`
        : ''
    }
    <div class="qr">
      <img src="${qr}" alt="QR code">
      <div class="cap"><b>${t('Scan to RSVP', 'დაასკანერე დასადასტურებლად')}</b>${esc(inviteUrl)}</div>
    </div>
    <div class="footer">${t('Powered by Storyfy.ge', 'Storyfy.ge-ით')}</div>
  </div>
  <script>
    window.onload = function () {
      // Let the web font load, then open the print dialog (Save as PDF).
      var go = function () { window.focus(); window.print(); };
      // onload does not wait for webfonts, and printing early loses the
      // Georgian face this document embeds. Cap the wait so a font that never
      // arrives cannot leave the user staring at a blank tab.
      if (document.fonts && document.fonts.ready) {
        var printed = false;
        var once = function () { if (!printed) { printed = true; go(); } };
        document.fonts.ready.then(function () { setTimeout(once, 120); });
        setTimeout(once, 3000);
      } else {
        setTimeout(go, 350);
      }
    };
  </script>
</body>
</html>`

  win.document.open()
  win.document.write(html)
  win.document.close()
}
